import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  AnalyticsPeriod,
  ClassroomMetrics,
  EngagementMetrics,
  OverviewQuestion,
  OverviewTheme,
  TrendDelta,
} from '../../models/analytics.types';
import {
  ViewerMetricsAnalyticsResponse,
  ViewerMetricsTangramOption,
  ViewerMetricsUserRow,
  ProfessorAnalyticsService,
} from '../../services/professor-analytics.service';
import { PAGE_SIZE } from '../../models/period-trends.const';

// Sentinel for the "All Tangrams" option in the dropdown. Matches the
// convention used by the Content Metrics tab.
const ALL_TANGRAMS = '';

@Component({
  selector: 'tangram-pa-viewer-metrics-tab',
  templateUrl: './viewer-metrics-tab.component.html',
  styleUrls: ['./viewer-metrics-tab.component.scss']
})
export class ViewerMetricsTabComponent implements OnInit, OnChanges {
  // Period flows down from the parent — single source of truth for the
  // dashboard-wide period dropdown.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = true;
  error = '';

  // Tangram filter — owned by this tab, scopes every metric server-side.
  selectedPathwayId: string = ALL_TANGRAMS;

  tangrams: ViewerMetricsTangramOption[] = [];
  classroomMetrics: ClassroomMetrics = {
    activeStudentsPct: 0,
    avgCompletionPerTangram: 0,
    avgTimePerTangram: '0m',
    avgTimePerContent: '0m',
  };
  engagementMetrics: EngagementMetrics = {
    commentsPerTangram: 0,
    pollCompletionRate: 0,
    questionCompletionRate: 0,
  };
  userEngagementRows: ViewerMetricsUserRow[] = [];
  topThemes: OverviewTheme[] = [];
  questionInsights: OverviewQuestion[] = [];
  trends: { [key: string]: TrendDelta } = {};

  readonly PAGE_SIZE = PAGE_SIZE;
  userEngagementPage = 1;

  // Cancel in-flight request before starting a new one — protects against
  // a fast period / Tangram switch overwriting fresh data with stale.
  private fetchSub?: Subscription;

  constructor(private analyticsService: ProfessorAnalyticsService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange)) {
      this.userEngagementPage = 1;
      this.load();
    }
  }

  // ── Filter handlers ─────────────────────────────────────────────
  onPathwayChange(pathwayId: string): void {
    this.selectedPathwayId = pathwayId;
    this.userEngagementPage = 1;
    this.load();
  }

  // ── Display helpers ─────────────────────────────────────────────
  trend(key: string): TrendDelta {
    return this.trends[key] || { delta: 0, unit: '' };
  }

  // The shared `<tangram-analytics-completion-bar>` and pagination
  // components only read the row fields by name, so the wider
  // `ViewerMetricsUserRow` shape (which carries `userId` for future
  // drill-downs) is rendered directly.
  get pagedUserEngagement(): ViewerMetricsUserRow[] {
    return this.userEngagementRows
      .slice(
        (this.userEngagementPage - 1) * this.PAGE_SIZE,
        this.userEngagementPage * this.PAGE_SIZE,
      );
  }

  get selectedTangramTitle(): string {
    if (!this.selectedPathwayId) return 'All Tangrams';
    const match = this.tangrams.find((t) => t.pathwayId === this.selectedPathwayId);
    return match ? match.title : 'Selected Tangram';
  }

  // ── Loading ─────────────────────────────────────────────────────
  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    this.loading = true;
    this.error = '';

    this.fetchSub = this.analyticsService
      .getViewerMetricsAnalytics(this.period, this.selectedPathwayId, this.startDate, this.endDate)
      .subscribe(
        (data: ViewerMetricsAnalyticsResponse) => {
          this.tangrams = data.tangrams || [];
          this.classroomMetrics = data.classroomMetrics || this.classroomMetrics;
          this.engagementMetrics = data.engagementMetrics || this.engagementMetrics;
          this.userEngagementRows = data.userEngagement || [];
          this.topThemes = data.topThemes || [];
          this.questionInsights = data.questionInsights || [];
          this.trends = data.trends || {};
          // If the server fell back to "all" because the supplied id
          // wasn't owned by the professor, keep the dropdown honest.
          if (data.filter && typeof data.filter.pathwayId === 'string') {
            this.selectedPathwayId = data.filter.pathwayId;
          }
          // Publish to the per-tab export cache (see service comment).
          this.analyticsService.setLatestTabData('viewer-metrics', data);
          this.loading = false;
        },
        (err) => {
          this.error = (err && err.message) ? err.message : 'Failed to load Viewer Metrics data.';
          this.loading = false;
        },
      );
  }
}
